
import type { StripeCheckout, StripePaymentElement } from '@stripe/stripe-js'
import type { FetchResult } from 'apollo-link'
import { defineComponent } from 'vue'

import CreateSetupCheckoutSessionMutation from '~/apollo/mutations/owner/createSetupCheckoutSession.gql'
import type { CreateSetupCheckoutSessionMutation as CreateSetupCheckoutSessionMutationData } from '~/types/types'
import { STRIPE_APPEARANCE } from '~/utils/stripeAppearance'

export default defineComponent({
  name: 'V2AddCardModal',
  props: {
    ownerId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      sessionLoading: true,
      sessionError: null as string | null,
      checkoutInstance: null as StripeCheckout | null,
      checkoutActions: null as any,
      paymentElement: null as StripePaymentElement | null,
      paymentReady: false,
      paymentComplete: false,
      processing: false,
      paymentError: null as string | null
    }
  },
  computed: {
    canSubmit(): boolean {
      return this.paymentReady && this.paymentComplete && !this.processing
    },
    stripeAppearance() {
      return STRIPE_APPEARANCE
    },
    returnUrl(): string {
      return `${window.location.origin}${this.$route.path}`
    }
  },
  mounted() {
    this.createSession()
  },
  beforeDestroy() {
    if (this.paymentElement) {
      this.paymentElement.destroy()
      this.paymentElement = null
    }
  },
  methods: {
    async createSession(): Promise<void> {
      this.sessionLoading = true
      this.sessionError = null

      try {
        const response = (await this.$applyGraphqlMutation(CreateSetupCheckoutSessionMutation, {
          ownerId: this.ownerId
        })) as FetchResult<CreateSetupCheckoutSessionMutationData>

        const clientSecret = response.data?.createSetupCheckoutSession?.clientSecret
        if (!clientSecret) {
          this.sessionError = 'Failed to create setup session. Please try again.'
          return
        }

        if (!this.$stripe) {
          this.sessionError = 'Payment form could not load. Please disable ad blockers and refresh.'
          return
        }

        this.checkoutInstance = await this.$stripe.initCheckout({
          clientSecret,
          elementsOptions: { appearance: this.stripeAppearance }
        })

        const actionsResult = await this.checkoutInstance.loadActions()
        if (actionsResult.type === 'error') {
          this.sessionError = actionsResult.error.message ?? 'Failed to load session.'
          return
        }

        this.checkoutActions = actionsResult.actions

        // Show the payment element container before mounting into it
        this.sessionLoading = false
        await this.$nextTick()
        this.mountPaymentElement()
      } catch (error) {
        this.sessionLoading = false
        this.sessionError = 'Failed to create setup session. Please try again.'
      }
    },

    mountPaymentElement(): void {
      if (!this.checkoutInstance) return

      if (this.paymentElement) {
        this.paymentElement.destroy()
        this.paymentElement = null
      }

      this.paymentReady = false
      this.paymentComplete = false
      this.paymentError = null

      this.paymentElement = this.checkoutInstance.createPaymentElement({
        layout: 'tabs'
      })
      this.paymentElement.mount('#v2-add-card-element')

      this.paymentElement.on('ready', () => {
        this.paymentReady = true
      })

      this.paymentElement.on('change', (event) => {
        this.paymentComplete = event.complete
      })
    },

    async saveCard(): Promise<void> {
      if (!this.checkoutActions) return

      this.processing = true
      this.paymentError = null

      try {
        const result = await this.checkoutActions.confirm({
          returnUrl: this.returnUrl,
          redirect: 'if_required'
        })

        if (result.type === 'error') {
          this.paymentError = result.error.message ?? 'Failed to save card. Please try again.'
        } else {
          this.$emit('card-added')
        }
      } catch (err) {
        this.paymentError = (err as Error).message ?? 'An unexpected error occurred.'
      } finally {
        this.processing = false
      }
    },

    close(): void {
      if (this.processing) return
      this.$emit('close')
    }
  }
})
